import React from "react";
import Ic from "./Ic";
import { bdg, sp, rad, type, icBox } from "../../constants/styles";

/**
 * FitBadge — full-width card section showing property fit analysis.
 * Shows tags, strengths (green checkmarks), and cautions (orange warnings).
 *
 * Props: { fit, h, size }
 *   fit  — from getPropertyFit()
 *   h    — theme object
 *   size — "sm" (compact inline) | "md" (full card section)
 */
export default function FitBadge({ fit, h, size = "sm" }) {
  if (!fit) return null;

  // --- Compact inline badge (for map pins, small cards) ---
  if (size === "sm") {
    return <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: `${fit.color}18`, color: fit.color,
      padding: "4px 10px", borderRadius: rad.sm,
      fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
      border: `1px solid ${fit.color}30`
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: fit.color, flexShrink: 0 }} />
      {fit.label} {fit.score > 0 && <span style={{ opacity: 0.7, fontWeight: 500 }}>({fit.score}%)</span>}
    </span>;
  }

  // --- Full card section (md) ---
  const gn = h.gn || "#30d158";
  const wn = h.wn || "#ff9f0a";

  return <div style={{
    background: `${fit.color}08`, borderRadius: rad.lg,
    border: `1px solid ${fit.color}25`, padding: sp.lg,
    marginBottom: sp.md
  }}>
    {/* Header: score bar + label */}
    <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.md }}>
      <div style={{
        ...icBox(fit.color, 40),
        background: `${fit.color}20`, borderRadius: rad.sm + 2
      }}>
        <Ic n="target" s={20} c={fit.color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
          <span style={{ ...type.h3, color: fit.color, margin: 0 }}>{fit.label}</span>
          <span style={{
            ...bdg(`${fit.color}20`, fit.color),
            fontSize: 13, fontWeight: 800, padding: "3px 12px"
          }}>{fit.score}%</span>
        </div>
        {/* Score bar */}
        <div style={{
          height: 6, borderRadius: 3, background: `${fit.color}15`,
          marginTop: 6, overflow: "hidden"
        }}>
          <div style={{
            height: "100%", borderRadius: 3, background: fit.color,
            width: `${fit.score}%`, transition: "width .4s ease"
          }} />
        </div>
      </div>
    </div>

    {/* Tags row */}
    {fit.tags && fit.tags.length > 0 && <div style={{
      display: "flex", flexWrap: "wrap", gap: 6, marginBottom: sp.md
    }}>
      {fit.tags.map((tag, i) => <span key={i} style={{
        ...bdg(`${fit.color}15`, fit.color),
        fontSize: 12, fontWeight: 600, padding: "5px 12px",
        borderRadius: rad.pill, border: `1px solid ${fit.color}25`
      }}>
        {tag}
      </span>)}
    </div>}

    {/* Strengths */}
    {fit.strengths && fit.strengths.length > 0 && <div style={{ marginBottom: fit.cautions?.length > 0 ? sp.sm : 0 }}>
      {fit.strengths.map((s, i) => <div key={i} style={{
        display: "flex", alignItems: "flex-start", gap: sp.sm,
        marginBottom: i < fit.strengths.length - 1 ? 6 : 0
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: "50%",
          background: `${gn}18`, display: "flex", alignItems: "center",
          justifyContent: "center", flexShrink: 0, marginTop: 1
        }}>
          <Ic n="check" s={13} c={gn} />
        </div>
        <span style={{ ...type.sm, color: h.tx, lineHeight: "22px" }}>{s}</span>
      </div>)}
    </div>}

    {/* Cautions */}
    {fit.cautions && fit.cautions.length > 0 && <div>
      {fit.cautions.map((c, i) => <div key={i} style={{
        display: "flex", alignItems: "flex-start", gap: sp.sm,
        marginBottom: i < fit.cautions.length - 1 ? 6 : 0
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: "50%",
          background: `${wn}18`, display: "flex", alignItems: "center",
          justifyContent: "center", flexShrink: 0, marginTop: 1
        }}>
          <Ic n="alert-triangle" s={13} c={wn} />
        </div>
        <span style={{ ...type.sm, color: h.ts || h.sc, lineHeight: "22px" }}>{c}</span>
      </div>)}
    </div>}
  </div>;
}
